import { z } from "zod";

// Comprehensive spam detection patterns
export const spamPatterns = [
  // Common spam keywords
  /\b(viagra|cialis|casino|lottery|winner|congratulations.*won|prince|inheritance)\b/i,
  // Call-to-action spam
  /\b(click here|act now|limited time|buy now|order now|free money|guaranteed)\b/i,
  // Suspicious TLDs
  /https?:\/\/[^\s]+\.(ru|cn|tk|ml|ga|cf)\b/i,
  // Repeated characters (5+ times)
  /(.)\1{4,}/i,
  // Money-making schemes
  /\b(make money fast|earn cash|work from home.*\$|earn \$\d+)\b/i,
  // Crypto scams
  /\b(crypto.*guaranteed|bitcoin.*profit|nft.*millionaire|investment opportunity)\b/i,
  // Unrealistic monetary amounts
  /\$\d{6,}/i,
];

/**
 * Check if text contains spam content
 */
export const containsSpamContent = (text: string): boolean => {
  return spamPatterns.some(pattern => pattern.test(text));
};

/**
 * Count the number of links in text
 */
export const countLinks = (text: string): number => {
  const urlPattern = /https?:\/\/[^\s]+/gi;
  return (text.match(urlPattern) || []).length;
};

/**
 * Check if text has excessive links
 */
export const hasExcessiveLinks = (text: string, maxLinks: number = 5): boolean => {
  return countLinks(text) > maxLinks;
};

/**
 * Check if email has suspicious domain
 */
export const hasSuspiciousEmailDomain = (email: string): boolean => {
  const suspiciousDomains = ['.ru', '.cn', '.tk', '.ml', '.ga', '.cf'];
  return suspiciousDomains.some(domain => email.toLowerCase().endsWith(domain));
};

// Reusable Zod refinements
export const noSpamContent = (message: string = "Content appears to contain spam") => 
  z.string().refine(val => !containsSpamContent(val), message);

export const noExcessiveLinks = (maxLinks: number = 5, message: string = "Too many links detected") =>
  z.string().refine(val => !hasExcessiveLinks(val, maxLinks), message);

// Common field schemas with spam protection
export const spamProtectedString = (options: {
  minLength?: number;
  maxLength?: number;
  fieldName?: string;
  maxLinks?: number;
}) => {
  const { minLength = 1, maxLength = 1000, fieldName = "Field", maxLinks } = options;
  
  const baseSchema = z.string()
    .trim()
    .min(minLength, `${fieldName} must be at least ${minLength} characters`)
    .max(maxLength, `${fieldName} must be less than ${maxLength} characters`);
  
  if (maxLinks !== undefined) {
    return baseSchema
      .refine(val => !containsSpamContent(val), `${fieldName} contains prohibited content`)
      .refine(val => !hasExcessiveLinks(val, maxLinks), `Too many links in ${fieldName.toLowerCase()}`);
  }
  
  return baseSchema.refine(val => !containsSpamContent(val), `${fieldName} contains prohibited content`);
};

// Validate submission timing (anti-bot)
export const validateSubmissionTiming = (
  startTime: number, 
  minSeconds: number = 3
): { valid: boolean; message?: string } => {
  const timeTaken = (Date.now() - startTime) / 1000;
  if (timeTaken < minSeconds) {
    return { 
      valid: false, 
      message: "Please take more time to fill out the form" 
    };
  }
  return { valid: true };
};
